import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Popover } from "antd";
import { URI } from "constants/uri";
import { ArrowRight, BarChart2, LineChart, Loader, Mail, Rocket, TrendingUp, Users } from "lucide-react";
import { UserContextData } from "ts-utils-lib";
import { CustomInputField, Heading1, ParagraphBody1, ParagraphBody2, PrimaryButton, Title1 } from "ts-widgets";

import { CustomerAnalyticsLogo } from "components/Logo/CustomerAnalyticsLogo";

import HowItWorks from "../../assets/images/HowItWorks.png";
import WelcomImage from "../../assets/welcome.svg";

export const CustomerAnalyticsLoginForm = ({
    setEmail,
    email,
    mutate,
    isLoading,
    isEmailSent,
    setIsEmailSent,
    name,
    setName,
    domain,
    setDomain,
    signupError,
    setSignupError
}: any) => {
    const [emailFromUrl, setEmailFromUrl] = useState("");
    const navigate = useNavigate();
    const [error, setError] = useState("");
    const location = useLocation();
    const urlParams = new URLSearchParams(window.location.search);
    const SOCIAL_LOGIN: string = process.env.SOCIAL_LOGIN || "";
    const [isValid, setIsValid] = useState(false);
    const [popoverHowItWorksVisible, setPopoverHowItWorksVisible] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [timer, setTimer] = useState(60);

    const startTimer = () => {
        setDisabled(true);
        setTimer(60);
        let interval = setInterval(() => {
            setTimer((prev) => {
                if (prev === 1) {
                    clearInterval(interval);
                    setDisabled(false);
                    return 60; // Reset timer for next click
                }
                return prev - 1;
            });
        }, 1000);
    };

    useEffect(() => {
        if (urlParams) {
            const value = urlParams.get("userEmail");
            if (value) {
                setIsValid(isValidEmail(value));
                setEmailFromUrl(value);
                setEmail(value);
                setDomain(value.split("@")[1] || "");
            }

            const planId = urlParams.get("planId") || "";
            localStorage.setItem("planId", planId);
        }

        const handleEnterKey = (event: KeyboardEvent) => {
            if (event.key === "Enter") {
                onContinue();
            }
        };

        window.addEventListener("keydown", handleEnterKey);

        return () => {
            window.removeEventListener("keydown", handleEnterKey);
        };
    }, [urlParams]);

    useEffect(() => {
        startTimer();
    }, []);

    const isValidEmail = (email: string) => {
        if (SOCIAL_LOGIN && SOCIAL_LOGIN === "enabled") {
            const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            const validMail = emailPattern.test(email);
            const emailRegexPattern =
                "^[a-zA-Z0-9._%+-]+@(?!gmail.com)(?!yahoo.com)(?!hotmail.com)(?!yahoo.co.in)(?!aol.com)(?!live.com)(?!tempmail.net)(?!guerrillamail.com)(?!outlook.com)(?!icloud.com)(?!protonmail.com)[a-zA-Z0-9_-]+.[a-zA-Z0-9-.]{2,61}$";
            const emailRegex = new RegExp(emailRegexPattern);
            const validMailWithSocial = emailRegex.test(email);

            if (!validMail) {
                setError("Invalid email, Try valid email");
            }
            if (!validMailWithSocial) {
                setError("Personal email not allowed, Try work email");
            }

            return validMail && validMailWithSocial;
        } else {
            const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            const IsValidTest = emailPattern.test(email);
            if (!IsValidTest) {
                setError("Invalid email, Try valid email");
            }
            return IsValidTest;
        }
    };

    const validateName = (name: string) => {
        const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿĀ-ſ]+(?:[\s-'][A-Za-zÀ-ÖØ-öø-ÿĀ-ſ]+)*$/;

        if (!nameRegex.test(name)) {
            setError("Name can only contain letters, spaces, and apostrophes.");
            return false;
        }

        return true;
    };

    const onContinue = () => {
        setIsEmailSent(false);
        if (setSignupError) setSignupError("");
        if (isValidEmail(email)) {
            mutate({ email, name, domain });
        }
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setEmail(value);
        setIsValid(isValidEmail(value));
        setDomain(value.split("@")[1] || "");
        setName("");
        if (setSignupError) setSignupError("");
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (isValid) {
            onContinue();
        }
    };

    const handlePopoverVisibility = (visible) => {
        setPopoverHowItWorksVisible(visible);
    };

    const popoverContent = (
        <div className="w-full max-w-[500px]">
            <img src={HowItWorks} alt="Tutorial" className="w-full h-auto rounded-md shadow-md" />
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-gray-900 dark:to-indigo-950 transition-colors duration-200 overflow-auto">
            {/* Responsive Header - Fixed to the top */}
            <div className="sticky top-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm z-50 border-b border-gray-100 dark:border-gray-800 transition-colors duration-200">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="h-16 flex items-center justify-between">
                        <CustomerAnalyticsLogo />
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="flex items-center gap-2">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    className="hidden sm:block">
                                    <path
                                        d="M9.75 17L9 20L8 21H16L15 20L14.25 17M3 13H21M5 17H19C20.1046 17 21 16.1046 21 15V5C21 3.89543 20.1046 3 19 3H5C3.89543 3 3 3.89543 3 5V15C3 16.1046 3.89543 17 5 17Z"
                                        stroke="#1238CE"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <a
                                    href="https://outlook.office365.com/book/ThriveStackCustomerAnalytics@thrivestack.ai/"
                                    className="text-[#1238CE] text-sm sm:text-base cursor-pointer"
                                    target="_blank">
                                    Schedule a Demo
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Area - Scrollable */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {!isEmailSent ? (
                    <div className="flex flex-col md:flex-row pt-4 pb-12">
                        {/* Left Column - Marketing Content - Hidden on small screens, visible from medium up */}
                        <div className="w-full md:w-3/5 lg:w-2/3 bg-gradient-to-br from-indigo-100 via-blue-50 to-indigo-50 rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none p-6 sm:p-8 md:p-12">
                            <div className="max-w-2xl">
                                <div className="inline-flex items-center space-x-2 px-3 py-1 sm:px-4 sm:py-2 bg-indigo-100 rounded-full mb-6 sm:mb-8">
                                    <Rocket className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                                    <span className="text-xs sm:text-sm font-medium text-indigo-700">
                                        Trusted by 500+ growing companies
                                    </span>
                                </div>

                                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-4 sm:mb-6 text-gray-900">
                                    Transform Your Customer Data Into{" "}
                                    <span className="text-indigo-600">Growth Insights</span>
                                </h1>

                                <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8">
                                    ThriveStack helps you understand your entire customer journey, from first touch to
                                    revenue, so you can make data-driven decisions that accelerate growth.
                                </p>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-12">
                                    <div className="bg-white/80 backdrop-blur-sm p-4 sm:p-6 rounded-xl shadow-sm">
                                        <div className="flex items-center space-x-3 text-indigo-600 mb-2 sm:mb-3">
                                            <LineChart className="w-5 h-5 sm:w-6 sm:h-6" />
                                            <h3 className="font-semibold">38% Higher ROI</h3>
                                        </div>
                                        <p className="text-sm sm:text-base text-gray-600">
                                            Connect every touchpoint to revenue and optimize your marketing spend.
                                        </p>
                                    </div>
                                    <div className="bg-white/80 backdrop-blur-sm p-4 sm:p-6 rounded-xl shadow-sm">
                                        <div className="flex items-center space-x-3 text-indigo-600 mb-2 sm:mb-3">
                                            <BarChart2 className="w-5 h-5 sm:w-6 sm:h-6" />
                                            <h3 className="font-semibold">52% Better Adoption</h3>
                                        </div>
                                        <p className="text-sm sm:text-base text-gray-600">
                                            Understand user behavior and connect product usage to revenue.
                                        </p>
                                    </div>
                                </div>

                                <div className="hidden sm:flex items-center space-x-4 sm:space-x-8">
                                    <div className="flex -space-x-2">
                                        <img
                                            src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=120"
                                            alt="User"
                                            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-white"
                                        />
                                        <img
                                            src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=120"
                                            alt="User"
                                            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-white"
                                        />
                                        <img
                                            src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=120"
                                            alt="User"
                                            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-white"
                                        />
                                    </div>
                                    <div className="text-xs sm:text-sm text-gray-600">
                                        Join 2,847+ professionals already growing with ThriveStack
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Signup Form */}
                        <div className="w-full md:w-2/5 lg:w-1/3 bg-white rounded-b-2xl md:rounded-r-2xl md:rounded-bl-none p-6 sm:p-8">
                            <div className="h-full flex flex-col">
                                <div className="flex-1">
                                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">
                                        Sign Up / Login
                                    </h2>

                                    <p className="text-sm sm:text-base text-gray-600 mb-1 sm:mb-2">
                                        Start your 14-day free trial.
                                    </p>
                                    <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
                                        No credit card required.
                                    </p>

                                    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                                        <div>
                                            <label
                                                htmlFor="email"
                                                className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                Work Email
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                                                </div>
                                                <input
                                                    type="email"
                                                    id="email"
                                                    value={email}
                                                    onChange={handleEmailChange}
                                                    className={`
                                                        block w-full pl-10 pr-3 py-2 
                                                        border rounded-lg 
                                                        focus:ring-2 focus:ring-indigo-600 focus:border-indigo-600
                                                        ${
                                                            isValid
                                                                ? "border-gray-300"
                                                                : email
                                                                ? "border-red-300"
                                                                : "border-gray-300"
                                                        }
                                                    `}
                                                    placeholder="you@company.com"
                                                />
                                            </div>
                                            {email && !isValid && (
                                                <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-red-600">
                                                    Please use your work email address
                                                </p>
                                            )}
                                            {signupError && (
                                                <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-red-600">
                                                    {signupError}
                                                </p>
                                            )}
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={!isValid || isLoading}
                                            className={`
                                                w-full inline-flex items-center justify-center space-x-2
                                                px-4 py-2 rounded-lg font-medium transition-colors
                                                ${
                                                    isValid
                                                        ? "bg-indigo-600 text-white hover:bg-indigo-700"
                                                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                }
                                            `}>
                                            {isLoading ? (
                                                <Loader className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                                            ) : (
                                                <>
                                                    <span>Get Started</span>
                                                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                                                </>
                                            )}
                                        </button>
                                    </form>

                                    {/* Stats - Now below the CTA button */}
                                    <div className="mt-4 sm:mt-6 flex justify-between sm:space-y-2 sm:block">
                                        <div className="flex items-center text-gray-600">
                                            <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                                            <span className="text-xs sm:text-sm">2,847 signups</span>
                                        </div>
                                        <div className="flex items-center text-gray-600">
                                            <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                                            <span className="text-xs sm:text-sm">4.9/5 rating</span>
                                        </div>
                                    </div>

                                    {/* Social proof for mobile only */}
                                    <div className="flex sm:hidden items-center mt-6 space-x-4">
                                        <div className="flex -space-x-2">
                                            <img
                                                src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=120"
                                                alt="User"
                                                className="w-7 h-7 rounded-full border-2 border-white"
                                            />
                                            <img
                                                src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=120"
                                                alt="User"
                                                className="w-7 h-7 rounded-full border-2 border-white"
                                            />
                                            <img
                                                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=120"
                                                alt="User"
                                                className="w-7 h-7 rounded-full border-2 border-white"
                                            />
                                        </div>
                                        <div className="text-xs text-gray-600">Join 2,847+ professionals</div>
                                    </div>
                                </div>

                                <div className="mt-6 sm:mt-8 pt-4 sm:pt-8 border-t border-gray-200">
                                    <p className="text-xs sm:text-sm text-gray-500 text-center">
                                        By signing up, you agree to our{" "}
                                        <a href="#" className="text-indigo-600 hover:text-indigo-700">
                                            Terms
                                        </a>{" "}
                                        and{" "}
                                        <a href="#" className="text-indigo-600 hover:text-indigo-700">
                                            Privacy Policy
                                        </a>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center pt-4 pb-12">
                        <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6 sm:p-8 mx-4">
                            <div className="text-center">
                                {/* Email Icon */}
                                <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                                    <Mail className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600" />
                                </div>

                                {/* Title */}
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                                    Check your inbox
                                </h2>

                                {/* Email Confirmation Message */}
                                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                                    We've sent a verification link to
                                    <br />
                                    <span className="font-semibold text-indigo-600">{email}</span>
                                </p>

                                {/* Instructions */}
                                <div className="space-y-2 sm:space-y-4 text-sm sm:text-base text-gray-600">
                                    <p>Click the link in the email to verify your account and get started.</p>
                                    <p>
                                        The verification link will expire in{" "}
                                        <span className="font-semibold">3 minutes</span>.
                                    </p>
                                    <p>If you don't see the email, check your spam folder.</p>
                                </div>

                                {/* Resend Button */}
                                <button
                                    onClick={() => {
                                        onContinue();
                                        startTimer(); // Restart timer on click
                                    }}
                                    disabled={disabled}
                                    className={`mt-6 sm:mt-8 w-full inline-flex items-center justify-center px-4 py-2 border rounded-lg transition-colors ${
                                        disabled
                                            ? "border-gray-300 text-gray-400 bg-gray-200 cursor-not-allowed"
                                            : "border-indigo-600 text-indigo-600 hover:bg-indigo-50"
                                    }`}>
                                    {disabled ? `Resend in ${timer}s` : "Resend verification email"}
                                </button>

                                {/* Help Links */}
                                <div className="mt-4 sm:mt-6 flex items-center justify-center space-x-4 text-xs sm:text-sm">
                                    <a href="#" className="text-gray-500 hover:text-indigo-600">
                                        Need help?
                                    </a>
                                    <span className="text-gray-300">|</span>
                                    <a href="#" className="text-gray-500 hover:text-indigo-600">
                                        Contact support
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
